// src/pages/RegisterPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (success) return;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    
    if (name === 'password') {
      let strength = 0;
      if (value.length >= 6) strength += 1;
      if (value.length >= 8) strength += 1;
      if (/[A-Z]/.test(value)) strength += 1;
      if (/[0-9]/.test(value)) strength += 1;
      if (/[^A-Za-z0-9]/.test(value)) strength += 1;
      setPasswordStrength(strength);
    }
    
    if (error) setError('');
  };

  const validateForm = () => {
    const { name, email, password, confirmPassword } = formData;

    if (!name.trim() || !email.trim() || !password || !confirmPassword) {
      setError('Todos los campos son obligatorios');
      return false;
    }
    
    if (name.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Por favor ingrese un email válido');
      return false;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    
    if (!acceptedTerms) {
      setError('Debe aceptar los términos y condiciones');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading || success) return;
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const result = await register({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (result.success) {
        setSuccess(true);
        navigate('/products', { replace: true });
      } else {
        setError(result.error || 'Error en el registro');
      }
    } catch (err) {
      console.error('Error en el registro:', err);
      setError(err.response?.data?.message || 'Error al registrar el usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mt-20 flex min-h-[calc(100vh-5rem)] items-center justify-center overflow-hidden px-4 py-4 bg-gradient-to-br from-[#F0F9F5] to-[#E0F3EB]">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#83F4E9]/35 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#F2A9FD]/25 blur-3xl" />
      <motion.div 
        className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/80 bg-white/90 shadow-2xl md:grid-cols-[0.9fr_1.1fr]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Encabezado */}
        <div className="hidden bg-gradient-to-br from-[#0C4B45] via-[#0b4741] to-[#083D38] px-10 py-8 text-center text-white md:flex md:flex-col md:items-center md:justify-center">
          <motion.div 
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/15 shadow-lg ring-1 ring-white/25"
            whileHover={{ scale: 1.05 }}
          >
            <UserIcon className="h-10 w-10 text-white" />
          </motion.div>
          <p className="mb-3 text-xs font-bold tracking-[0.3em] text-[#83F4E9]">PCVASQUEZ</p>
          <h2 className="text-2xl font-bold leading-tight">Tu tecnología, más cerca</h2>
          <p className="mt-3 max-w-sm text-sm font-light text-white/80">Crea tu cuenta para comprar, guardar favoritos y seguir tus pedidos.</p>
        </div>

        {/* Tarjeta de Registro */}
        <motion.div 
          className="bg-white"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 sm:p-7 lg:px-10 lg:py-7">
            <div className="mb-5">
              <p className="text-xs font-bold tracking-[0.25em] text-[#0C4B45] md:hidden">PCVASQUEZ</p>
              <h2 className="mt-2 text-2xl font-bold text-[#0C4B45]">Crear cuenta</h2>
              <p className="mt-1 text-sm text-[#4A6B57]">Regístrate en menos de un minuto.</p>
            </div>
            {/* ✅ MENSAJE DE ÉXITO VERDE */}
            {success && (
              <motion.div 
                className="mb-5 sm:col-span-2 p-4 rounded-lg bg-green-50 border border-green-200 flex items-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <div>
                  <span className="text-green-800 font-medium block">
                    ¡Registro exitoso!
                  </span>
                  <span className="text-green-700 text-sm block mt-1">
                    Tu sesión fue iniciada. Redirigiendo...
                  </span>
                </div>
              </motion.div>
            )}

            {/* ❌ MENSAJE DE ERROR ROJO - Solo mostrar si NO hay éxito */}
            {error && !success && (
              <motion.div 
                className="mb-5 sm:col-span-2 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
                <span className="text-red-800">{error}</span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Campos del formulario (igual que antes) */}
              <div>
                <label className="block text-sm font-medium text-[#4A6B57] mb-1">
                  Nombre completo
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-3 h-5 w-5 text-[#0C4B45]" />
                  <input
                    name="name"
                    type="text"
                    required
                    disabled={loading || success}
                    className="w-full pl-10 pr-4 py-3 border border-[#83F4E9] rounded-xl bg-[#83F4E9]/10 focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Tu nombre completo"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A6B57] mb-1">
                  Correo electrónico
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-[#0C4B45]" />
                  <input
                    name="email"
                    type="email"
                    required
                    disabled={loading || success}
                    className="w-full pl-10 pr-4 py-3 border border-[#83F4E9] rounded-xl bg-[#83F4E9]/10 focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A6B57] mb-1">
                  Contraseña
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-[#0C4B45]" />
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    disabled={loading || success}
                    className="w-full pl-10 pr-10 py-3 border border-[#83F4E9] rounded-xl bg-[#83F4E9]/10 focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Mínimo 6 caracteres"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading || success}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-[#0C4B45]" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-[#0C4B45]" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A6B57] mb-1">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-[#0C4B45]" />
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    disabled={loading || success}
                    className="w-full pl-10 pr-10 py-3 border border-[#83F4E9] rounded-xl bg-[#83F4E9]/10 focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Confirma tu contraseña"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading || success}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-[#0C4B45]" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-[#0C4B45]" />
                    )}
                  </button>
                </div>
              </div>

              <div className={`sm:col-span-2 rounded-xl border p-3 transition-colors ${acceptedTerms ? 'border-[#83F4E9] bg-[#83F4E9]/10' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-start">
                <input
                  id="acceptedTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                  disabled={loading || success}
                  className="mt-1 h-4 w-4 text-[#0C4B45] focus:ring-[#0C4B45] border-[#83F4E9] rounded disabled:bg-gray-100"
                />
                <label htmlFor="acceptedTerms" className="ml-3 text-sm text-[#0C4B45] cursor-pointer leading-relaxed">
                  <span className="font-semibold">Acepto los términos y condiciones</span>
                  <span className="block text-xs text-[#4A6B57] mt-1">Confirmo que leí y acepto el tratamiento de mis datos para gestionar mi cuenta y pedidos.</span>
                </label>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loading || success}
                className="sm:col-span-2 w-full flex justify-center items-center py-3 px-6 bg-gradient-to-r from-[#0C4B45] to-[#083D38] text-white rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#0C4B45] focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={!(loading || success) ? { scale: 1.02 } : {}}
                whileTap={!(loading || success) ? { scale: 0.98 } : {}}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creando cuenta...
                  </>
                ) : success ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-white mr-2" />
                    Registro Completado
                  </>
                ) : (
                  <>
                    Registrarme
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </>
                )}
              </motion.button>
            </form>

            <div className="mt-5 text-center">
              <p className="text-sm text-[#4A6B57]">
                ¿Ya tienes una cuenta?{' '}
                <Link 
                  to="/login" 
                  className="font-medium text-[#662D8F] hover:underline"
                >
                  Inicia sesión aquí
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
